import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineListUlIcon from 'LuxKit/Icons/line/LineListUlIcon'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import PlaceInfoDrawer from '../components/PlaceInfoDrawer'
import { MARKER_TYPE_STOP } from 'tripPlanner/utils/mapMarkerTransforms'
import {
  RightPanel,
  RightPanelHeader,
  RightPanelContent,
  EmptyStateWrapper,
} from '../styles/layoutStyles'
import type { RightPanelView } from '../hooks/useRightPanelState'
import { useRightPanelState } from '../hooks/useRightPanelState'
import { useIsCartBetaUser } from '../hooks/useIsCartBetaUser'
import { useEventSubscription } from '../contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { MODAL_TRANSITION_DURATION } from 'LuxKit/Modal/modalConstants'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import MapContainer from './MapContainer'
import ItineraryContainer from './ItineraryContainer'
import ListContainer from './ListContainer'
import CartPanelContainer from './CartPanelContainer'

function RightPanelContainer() {
  const intl = useIntl()
  const { sendMessage } = useChatActions()
  const { itineraryData, hasItineraryData } = useItineraryContext()
  const isCartBetaUser = useIsCartBetaUser()

  const {
    rightPanelView,
    setRightPanelView,
    selectedDayItineraryId,
    setSelectedDayItineraryId,
    selectedMarkerData,
    handleMarkerSelect,
    handleMarkerOverlayClose,
  } = useRightPanelState()

  // ── Computed state ──

  const selectedStop = useMemo(() => {
    if (!selectedDayItineraryId || !itineraryData) {
      return undefined
    }
    // Find the stop that contains this day
    for (const stop of itineraryData.stops) {
      const dayExists = stop.days.some((day: { id: number }) => day.id === selectedDayItineraryId)
      if (dayExists) {
        return stop
      }
    }
    return undefined
  }, [selectedDayItineraryId, itineraryData])

  const shouldShowMap = rightPanelView === 'map'
  const isTimelineView = rightPanelView === 'timeline'
  const isListView = isCartBetaUser && rightPanelView === 'list'
  const shouldShowTimelineEmptyState = isTimelineView && !hasItineraryData
  const shouldShowTripPlanCanvas = isTimelineView && hasItineraryData
  const shouldShowListView = isListView && hasItineraryData

  // ── Cart panel animation state (TripDrawer pattern) ──

  const isCartView = rightPanelView === 'cart'
  const [shouldRenderCart, setShouldRenderCart] = useState(isCartView)
  const [isCartClosing, setIsCartClosing] = useState(false)
  const preCartViewRef = useRef<Exclude<RightPanelView, 'cart'>>('map')

  useEffect(() => {
    if (isCartView) {
      setShouldRenderCart(true)
      setIsCartClosing(false)
    } else if (shouldRenderCart) {
      setIsCartClosing(true)
      const timer = setTimeout(() => {
        setShouldRenderCart(false)
        setIsCartClosing(false)
      }, MODAL_TRANSITION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isCartView, shouldRenderCart])

  const hideToggle = shouldRenderCart

  // ── View toggle ──

  const handleViewToggleWithCleanup = useCallback((view: RightPanelView) => {
    setRightPanelView(view)
  }, [setRightPanelView])

  const handleCartClose = useCallback(() => {
    setRightPanelView(preCartViewRef.current)
  }, [setRightPanelView])

  // ── Event bus subscriptions ──

  useEventSubscription('open_cart_panel', () => {
    if (!isCartBetaUser) return
    setRightPanelView((prev) => {
      if (prev === 'cart') return preCartViewRef.current
      preCartViewRef.current = prev
      return 'cart'
    })
  })

  useEventSubscription('reset_chat_state', () => {
    setRightPanelView('map')
    setSelectedDayItineraryId(undefined)
    handleMarkerOverlayClose()
  })

  // ── i18n ──

  const itineraryOptionTitle = intl.formatMessage({
    id: 'ktG8P0ofvtLiBqqJLzYX',
    defaultMessage: 'Itinerary',
    description: 'Segmented option title for the itinerary view in the trip planner right panel',
  })

  const timelineOptionTitle = intl.formatMessage({
    id: 'CrXe4aeBNf4hdDEigM3u',
    defaultMessage: 'Timeline',
    description: 'Segmented option title for the timeline view in the trip planner right panel',
  })

  const listOptionTitle = intl.formatMessage({
    id: 'mgKCGdrsVCcLCxmhhoN-',
    defaultMessage: 'List',
    description: 'Segmented option title for the list view in the trip planner right panel',
  })

  const mapOptionTitle = intl.formatMessage({
    id: '3uJcGV6ZPs3TPDucD-0n',
    defaultMessage: 'Map',
    description: 'Segmented option title for the map view in the trip planner right panel',
  })

  const emptyStateHeading = intl.formatMessage({
    id: '9S1WS26ztBJ5qIocVrOD',
    defaultMessage: 'As we chat, I\u2019ll start building your trip here.',
    description: 'Empty state heading on the trip planner itinerary panel before any itinerary is created',
  })

  const emptyStateBody = intl.formatMessage({
    id: 'iKg1dzi0QzxuadaLeDj4',
    defaultMessage: 'You\u2019ll see a flexible itinerary take shape \u2014 with suggested days and experiences, ready for you to tweak, swap, or explore anytime.',
    description: 'Empty state body text on the trip planner itinerary panel before any itinerary is created',
  })

  const tripPlanningIllustrationAlt = intl.formatMessage({
    id: 'IOcq4TKUjnNxvkw51aTh',
    defaultMessage: 'Trip planning illustration',
    description: 'Alt text for the trip planning illustration image in the itinerary empty state',
  })

  return (
    <RightPanel>
      {!hideToggle && (
        <RightPanelHeader>
          <SegmentedOptionsGroup
            inputsName="rightPanelView"
            onValueChange={handleViewToggleWithCleanup}
          >
            <SegmentedOption
              value="timeline"
              title={isCartBetaUser ? timelineOptionTitle : itineraryOptionTitle}
              icon={<LineClockIcon size="XS" />}
              checked={rightPanelView === 'timeline'}
            />
            {isCartBetaUser && (
              <SegmentedOption
                value="list"
                title={listOptionTitle}
                icon={<LineListUlIcon size="XS" />}
                checked={rightPanelView === 'list'}
              />
            )}
            <SegmentedOption
              value="map"
              title={mapOptionTitle}
              icon={<LineMapIcon size="XS" />}
              checked={rightPanelView === 'map'}
            />
          </SegmentedOptionsGroup>
        </RightPanelHeader>
      )}
      <RightPanelContent>
        <ErrorBoundary fallback={null}>
          <MapContainer
            visible={shouldShowMap}
            selectedDayItineraryId={selectedDayItineraryId}
            selectedStop={selectedStop}
            selectedMarkerData={selectedMarkerData}
            onMarkerSelect={handleMarkerSelect}
            setRightPanelView={setRightPanelView}
            rightPanelView={rightPanelView}
          />
        </ErrorBoundary>
        {shouldShowTimelineEmptyState && (
          <EmptyStateWrapper padding={40}>
            <Group direction="vertical" gap={16} horizontalAlign="center">
              <Image id="kng038msa1ltxqwyjvj" width={200} height={200} alt={tripPlanningIllustrationAlt} format="gif" />
              <Group direction="vertical" gap={8} horizontalAlign="center">
                <Heading variant="heading5">{emptyStateHeading}</Heading>
                <BodyText variant="medium" colour="light-foreground-alt" align="center">
                  {emptyStateBody}
                </BodyText>
              </Group>
            </Group>
          </EmptyStateWrapper>
        )}
        <ItineraryContainer
          visible={shouldShowTripPlanCanvas}
          selectedDayItineraryId={selectedDayItineraryId}
          selectedStop={selectedStop}
        />
        {isListView && !hasItineraryData && (
          <EmptyStateWrapper padding={40}>
            <Group direction="vertical" gap={16} horizontalAlign="center">
              <Image id="kng038msa1ltxqwyjvj" width={200} height={200} alt={tripPlanningIllustrationAlt} format="gif" />
              <Group direction="vertical" gap={8} horizontalAlign="center">
                <Heading variant="heading5">{emptyStateHeading}</Heading>
                <BodyText variant="medium" colour="light-foreground-alt" align="center">
                  {emptyStateBody}
                </BodyText>
              </Group>
            </Group>
          </EmptyStateWrapper>
        )}
        {isCartBetaUser && <ListContainer visible={shouldShowListView} />}
      </RightPanelContent>
      {shouldRenderCart && (
        <CartPanelContainer
          isClosing={isCartClosing}
          onClose={handleCartClose}
        />
      )}
      {selectedMarkerData?.marker?.placeId && (
        <PlaceInfoDrawer
          isOpen={Boolean(selectedMarkerData)}
          onClose={handleMarkerOverlayClose}
          googlePlaceId={selectedMarkerData.marker.placeId}
          placeName={selectedMarkerData.marker.title || selectedMarkerData.marker.label || ''}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          hideAddToTrip={selectedMarkerData.marker.markerType === MARKER_TYPE_STOP}
        />
      )}
    </RightPanel>
  )
}

export default RightPanelContainer
